import { useEffect, useState } from 'react'
import { Theme, Typography, Link as MuiLink } from '@material-ui/core'
import { Link } from '@src/components/Link'
import { chromatogramRoute } from '@src/routes'
import HandleMarkup from '@src/components/HandleMarkup'
import { FormattedMessage } from 'react-intl'
import messages from '@src/utils/messages'
import { makeStyles } from '@material-ui/core/styles'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import { Product } from '@src/types/graphql-types'

const useStyles = makeStyles((theme: Theme) => ({
  typeHeader: {
    marginTop: theme.spacing(7),
    marginBottom: theme.spacing(4),
  },
  itemTitle: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: theme.typography.fontWeightBold,
  },
  itemBody: {
    marginBottom: theme.spacing(5),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
  },
  showMoreLink: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: theme.typography.fontWeightBold,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
}))

interface ChromatogramProps {
  chromatograms: Product[]
}

// requirement in pdp-257
const DEFAULT_ITEMS_TO_SHOW = 4
const MAX_ITEMS_TO_SHOW = 10

const Chromatograms: React.FC<ChromatogramProps> = ({ chromatograms }) => {
  const numberOfChromatograms = chromatograms.length

  const getNumberOfItemsToShow = () =>
    numberOfChromatograms > DEFAULT_ITEMS_TO_SHOW
      ? DEFAULT_ITEMS_TO_SHOW
      : numberOfChromatograms

  const [showMoreToggle, setShowMoreToggle] = useState(false)
  const [numberOfItemsToShow, setNumberOfItemsToShow] = useState(
    getNumberOfItemsToShow()
  )

  const shouldDisplayShowMoreLink =
    numberOfChromatograms > DEFAULT_ITEMS_TO_SHOW

  useEffect(() => {
    if (showMoreToggle) {
      setNumberOfItemsToShow(MAX_ITEMS_TO_SHOW)
    } else {
      setNumberOfItemsToShow(getNumberOfItemsToShow())
    }
  }, [showMoreToggle])

  const classes = useStyles()

  return (
    <>
      <Typography variant="h3" className={classes.typeHeader}>
        <FormattedMessage {...messages.CHROMATOGRAMS} />
      </Typography>
      {chromatograms.slice(0, numberOfItemsToShow).map((chromatogram) => {
        return (
          <>
            <Typography
              component="div"
              data-testid="chromatogram-name"
              id="chromatogram-name"
            >
              <Link
                {...chromatogramRoute.index(
                  chromatogram.productNumber,
                  chromatogram.brand.key,
                  chromatogram.name,
                  chromatogram.cardAttribute?.application[0]
                )}
                passHref
              >
                <MuiLink color="primary">
                  <HandleMarkup value={chromatogram.name} />
                </MuiLink>
              </Link>
            </Typography>

            <Typography
              variant="body2"
              component="span"
              className={classes.itemBody}
            >
              <HandleMarkup value={chromatogram.description} />
            </Typography>
          </>
        )
      })}

      {shouldDisplayShowMoreLink && (
        <Typography
          component="a"
          color="primary"
          className={classes.showMoreLink}
          onClick={() => setShowMoreToggle(!showMoreToggle)}
        >
          {showMoreToggle ? (
            <>
              <FormattedMessage {...messages.SHOW_LESS} />
              <KeyboardArrowUpIcon />
            </>
          ) : (
            <>
              <FormattedMessage {...messages.SHOW_MORE} />
              <KeyboardArrowDownIcon />
            </>
          )}
        </Typography>
      )}
    </>
  )
}

export default Chromatograms
